"use client";
import React from "react";
import Image from "next/image";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  useTheme,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import Link from "next/link";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const HeroSectionStart = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const featuresRaw = t("heroSectionStart.features", { returnObjects: true });
  const features = Array.isArray(featuresRaw) ? featuresRaw : [];
  const featureImages = [IMAGES.HS_ONE, IMAGES.HS_TWO, IMAGES.HS_THREE];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: "4%",
        mt: isArabic ? { xs: "-8%", sm: "-2%" } : "-2%",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        <Card
          elevation={0}
          sx={{
            borderRadius: "10px",
            background: "rgba(255, 255, 255, 0.9)",
            overflow: "visible",
            padding: "1px",
            backgroundImage:
              "linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), linear-gradient(to bottom, #FFFFFF, #D6BAFF)",
            backgroundOrigin: "padding-box, border-box",
            backgroundClip: "padding-box, border-box",
          }}
        >
          <CardContent sx={{ p: { xs: 4, md: 6 } }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
                gap: 6,
                alignItems: "center",
              }}
            >
              {/* Left Content */}
              <Box>
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    fontSize: { xs: "2rem", md: "35px" },
                    fontWeight: "bold",
                    fontFamily: "var(--font-plusjakarta)",
                    mb: 3,
                    color: "#0A0A0A",
                    lineHeight: 1.4,
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("heroSectionStart.title1")}{" "}
                  <Box component="span" sx={{ color: "#775CE5" }}>
                    {t("heroSectionStart.titleHighlight1")}
                  </Box>{" "}
                  {t("heroSectionStart.title2")}{" "}
                  <Box component="span" sx={{ color: "#775CE5" }}>
                    {t("heroSectionStart.titleHighlight2")}
                  </Box>
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#717182",
                    mb: 8,
                    fontSize: "1.1rem",
                    lineHeight: 1.5,
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 400,
                    maxWidth: 500,
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("heroSectionStart.subtitle")}
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  mb={5}
                  sx={{
                    justifyContent: isArabic ? "flex-end" : "flex-start",
                    gap: { xs: "12px", sm: "16px" },
                    flexWrap: "nowrap",
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  {/* Button 1 - Purple */}
                  <Link href="/free-trial-of-ai-recruiting-tool" passHref>
                    <Button
                      sx={{
                        background: "#7C3AED",
                        color: "white",
                        px: { xs: 2.5, sm: 3 },
                        py: 1.5,
                        borderRadius: 50,
                        textTransform: "none",
                        fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                        fontWeight: 400,
                        fontFamily: "var(--font-outfit)",
                        boxShadow: "none",
                        whiteSpace: "nowrap",
                        minWidth: "max-content",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        "&:hover": { background: "#000", boxShadow: "none" },
                      }}
                    >
                      {/* {isArabic && (
                      <ArrowForwardIcon
                        sx={{ fontSize: "1.1rem", transform: "scaleX(-1)" }}
                      />
                    )} */}
                      {t("heroSectionStart.cta1")}
                      {!isArabic && (
                        <ArrowForwardIcon sx={{ fontSize: "1.1rem" }} />
                      )}
                    </Button>
                  </Link>

                  {/* Button 2 - Outlined */}
                  <Link href="/free-trial-of-ai-recruiting-tool" passHref>
                    <Button
                      sx={{
                        px: { xs: 2.5, sm: 3 },
                        py: 1,
                        borderRadius: 50,
                        textTransform: "none",
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                        fontWeight: 600,
                        border: "3px solid #f0e9fd",
                        fontFamily: "var(--font-outfit)",
                        color: "#7C3AED",
                        whiteSpace: "nowrap",
                        minWidth: "max-content",
                        "&:hover": {
                          boxShadow: "none",
                          backgroundColor: "transparent",
                          color: "#000",
                          border: "3px solid #000",
                        },
                      }}
                    >
                      {t("heroSectionStart.cta2")}
                    </Button>
                  </Link>
                </Stack>

                <Typography
                  variant="caption"
                  sx={{
                    color: "#717182",
                    fontSize: "0.875rem",
                    fontFamily: "var(--font-outfit)",
                    fontWeight: 400,
                    display: "block",
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {t("heroSectionStart.note")}
                </Typography>
              </Box>

              {/* Right Content - Feature Cards */}
              <Box
                sx={{
                  backgroundImage: `url('${IMAGES.GRADIENT_BG}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  borderRadius: 4,
                  p: 3,
                  border: "2px solid #f9f3ff",
                }}
              >
                <Stack spacing={2}>
                  {features.map((feature, index) => (
                    <Card
                      key={index}
                      elevation={0}
                      sx={{
                        background: "#FFFFFF",
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        border: "1px solid #F3E8FF",
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Stack
                          direction={isArabic ? "row-reverse" : "row"}
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <Box
                            sx={{
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 48,
                              height: 48,
                              position: "relative",
                            }}
                          >
                            <Image
                              src={featureImages[index]}
                              alt={feature.title}
                              width={45}
                              height={45}
                              style={{ objectFit: "contain" }}
                            />
                          </Box>
                          <Box sx={{ textAlign: isArabic ? "right" : "left" }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: "16px",
                                fontWeight: 400,
                                color: "#0A0A0A",
                                mb: 0.5,
                                fontFamily: "var(--font-outfit)",
                              }}
                            >
                              {feature.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#717182",
                                fontSize: "13px",
                                fontFamily: "var(--font-outfit)",
                                fontWeight: 400,
                              }}
                            >
                              {feature.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default HeroSectionStart;
